/**
 * @author: Wangwei
 * @description: 打卡营学习可操作模式
 */
import { useRequest } from 'umi';
import styles from '@/pages/camp/css/studyIndexCSS.less';
import React, { useState, useEffect, useRef, memo, useMemo } from 'react';
import cx from 'classnames';
import { Form, Button, Rate, message, Anchor, Skeleton, Modal, notification } from 'antd';
import { getNodeContent, postNode, postNodeSave } from '@/utils/requests/camp/requests_study';
import { NodeContentType } from '@/types/camp/study/index';
import {
  NodeContentTypes,
  ContentTypes,
  TextType,
  VideoType,
  CertificationType,
  VideoLogParamsType,
} from '@/types/camp/study/contentTypes';
import { CheckCircleOutlined, SyncOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Videos from './video';
import Cert from './cert';
import Question from './question';
import Editor from '@digit-fe/digit-editor-new';
import { debounce } from 'lodash';
import { editParseJson } from '@/utils/index';
import { onFileAndVideoUpload, onImageUpload } from '@/pages/camp/components/editor/uploadUtil';

interface Props {
  nodeId: string;
}

const StudyEdit: React.FC<Props> = ({ nodeId }) => {
  const [nodeContentData, setNodeContentData] = useState<NodeContentTypes>();
  const [videoLogParams, setVideoLogParams] = useState<VideoLogParamsType>();
  const [contentData, setContentData] = useState<ContentTypes[]>([]);
  // 习题表单数据操作
  const [exercises, setExercises] = useState<ContentTypes[]>([]);
  const [form] = Form.useForm();
  const [rateStudyContent, setRateStudyContent] = useState<number>(0);
  const [rateExerciseTask, setRateExerciseTask] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  // ref
  const rateStudyContentRef = useRef<any>(0);
  const rateExerciseTaskRef = useRef<any>(0);
  const dynamicContentRef = useRef<any>('');
  const rateContentRef = useRef<any>('');

  const [saveText, setSaveText] = useState('');
  const [cacheName, setCacheName] = useState('');

  const [newNotification, contextHolder] = notification.useNotification();
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 评分数据
  const rateText = ['非常不满意', '不满意', '一般', '满意', '非常满意'];
  useEffect(() => {
    setRateStudyContent(nodeContentData?.studyNode.rateStudyContent || 0);
    setRateExerciseTask(nodeContentData?.studyNode.rateExerciseTask || 0);
  }, [nodeContentData]);

  // 提交节点
  const postNodeRequest = useRequest(
    (nodeContentData: any) => {
      return postNode(nodeContentData);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setConfirmLoading(true);
        setTimeout(function () {
          // 刷新页面
          window.location.reload();
        }, 500);
      },
      onError: (e) => {},
    },
  );
  // 保存节点
  const postNodeSaveRequest = useRequest(
    (nodeContentData: any) => {
      return postNodeSave(nodeContentData);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setSaveText('自动保存成功');
        newNotification.destroy();
      },
      onError: (e) => {
        if (e instanceof Error) {
          openNotificationWithIcon();
          setSaveText('网络异常，将在网络恢复后自动保存');
        }
      },
    },
  );

  // 获取节点模块内容
  const { run: getNodeContentRequest } = useRequest(
    (nodeId) => {
      return getNodeContent(nodeId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setNodeContentData(res);
        // 视频埋点所需参数
        const parames: VideoLogParamsType = {
          batchId: res?.studyNode.batchId,
          nodeId: res?.id,
        };

        setVideoLogParams(parames);
        localStorage.setItem(cacheName, JSON.stringify(res));
      },
    },
  );

  useEffect(() => {
    if (nodeId) {
      setSaveText('');
      setLoading(true);
      setCacheName(`${nodeId}_nodeContentData`);
      getNodeContentRequest(nodeId);
    }
  }, [nodeId]);

  useEffect(() => {
    // 提取模块内容
    setContentData(nodeContentData?.contents || []);
    setTimeout(function () {
      nodeContentData?.id && setLoading(false);
    }, 600);
  }, [nodeContentData]);

  // 悬浮栏目导航
  const anchorItems = contentData.map((item, index) => ({
    key: item.id,
    href: `#${item.id}`,
    title: item.name,
  }));
  anchorItems.push(
    {
      key: 'rate_camp',
      href: '#rate_camp',
      title: '打卡心得',
    },
    {
      key: 'rate_study',
      href: '#rate_study',
      title: '学习评价',
    },
  );

  // 处理不同板块内容
  const handleContentItem = (item: ContentTypes, index: number): React.ReactNode | null => {
    let result: React.ReactNode | null = null;
    switch (item.type) {
      case NodeContentType.RICH_TEXT:
        if (item.info && typeof item.info === 'object') {
          result = (
            <Editor
              content={editParseJson((item.info as TextType).richText)}
              editable={false}
              titleProps={{ showTitle: false }}
              catalogProps={{ showCatalog: false }}
            />
          );
        }
        break;
      case NodeContentType.EXERCISES:
        if (item.info && typeof item.info === 'object') {
          result = (
            <Question item={item} handleAddItemOrUpdate={handleAddItemOrUpdate} form={form} />
          );
        }
        break;
      case NodeContentType.VIDEO:
        if (item.info && typeof item.info === 'object') {
          result = (
            <Videos
              videoList={item.info as VideoType}
              videoLogParams={videoLogParams}
              contentId={item.id}
            />
          );
        }
        break;
      case NodeContentType.PROFESSIONAL_CERTIFICATION:
        if (item.info) {
          result = <Cert data={item.info as CertificationType} form={form} />;
        }
        break;
      default:
        result = null;
    }

    result = (
      <div style={{ marginBottom: 40 }}>
        <div id={item.id} className="f24 fw600" style={{ marginBottom: 16 }}>
          {item.name}
        </div>
        <Form form={form} key={index} layout="vertical">
          {result}
        </Form>
      </div>
    );

    return result;
  };

  const openNotificationWithIcon = () => {
    newNotification.warning({
      message: '网络异常',
      description: '别着急，你可以继续离线编辑。内容将在联网后自动保存到云端，请勿清理浏览器缓存。',
      duration: 0,
    });
  };

  // 保存习题
  const handleAddItemOrUpdate = (newItem: ContentTypes) => {
    const newExercises = exercises.find((item) => item.id === newItem.id);
    if (newExercises) {
      const updatedExercises = exercises.map((item) =>
        item.id === newItem.id ? { ...item, ...newItem } : item,
      );
      setExercises(updatedExercises);
    } else {
      setExercises([...exercises, newItem]);
    }
  };
  useEffect(() => {
    console.log('exercises', exercises);
    saveCache();
  }, [exercises]);

  // 自动保存
  useEffect(() => {
    const submitTimer = () => {
      setSaveText('正在保存');
      let storedArray = editParseJson(localStorage.getItem(cacheName) || '');
      storedArray && postNodeSaveRequest.run(storedArray);
    };
    const intervalId = setInterval(submitTimer, 60000);
    return () => clearInterval(intervalId);
  }, [cacheName]);

  // 节流保存
  let debouncedUpdate = debounce(() => {
    saveCache();
  }, 500);

  // 实时保存至浏览器缓存
  const saveCache = () => {
    if (nodeContentData) {
      // 习题
      nodeContentData.contents = contentData;
      exercises.forEach((newItem) => {
        // 用户操作的习题替换进去 (id相同)
        let indexToReplace = nodeContentData.contents.findIndex((obj) => obj.id === newItem.id);
        if (indexToReplace !== -1) {
          nodeContentData.contents[indexToReplace] = newItem;
        }
      });
      // 心得
      nodeContentData.nodeDynamic = {
        ...nodeContentData.nodeDynamic,
        batchId: nodeContentData.studyNode.batchId,
        cardCampId: nodeContentData.cardCampId,
        content: dynamicContentRef?.current || nodeContentData.nodeDynamic.content,
        nodeId: nodeContentData.id,
        praiseCount: 0,
      };
      // 评分
      nodeContentData.studyNode = {
        ...nodeContentData.studyNode,
        rateStudyContent: rateStudyContentRef.current || nodeContentData.studyNode.rateStudyContent,
        rateExerciseTask:
          rateExerciseTaskRef?.current || nodeContentData.studyNode.rateExerciseTask,
        rateContent: rateContentRef?.current || nodeContentData.studyNode.rateContent,
      };

      // 存储到浏览器缓存
      localStorage.setItem(cacheName, JSON.stringify(nodeContentData));
    }
  };

  // 学习评价校验
  const validateRateStudy = () => {
    if (nodeContentData?.remark) {
      if (rateStudyContent <= 0 || rateExerciseTask <= 0) {
        message.warning('学习评价打分必填');
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  // 必填滚动
  const handleFinishFailed = (errorInfo: any) => {
    if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
      const firstErrorField = errorInfo.errorFields[0];
      console.log('必填项目', firstErrorField);
      const firstErrorElement = document.getElementById(firstErrorField.name + '_title');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // 构造表单提交
  const handleSubmit = async () => {
    try {
      // 验证习题
      await form.validateFields();

      // 验证学习评价
      if (!validateRateStudy()) {
        return false;
      }

      // 提交接口
      // console.log('提交接口==', nodeContentData);

      let subjectQuestion =
        nodeContentData?.manualReview && nodeContentData?.countSubjectQuestion
          ? `本节有 ${nodeContentData?.countSubjectQuestion} 道主观题需要助教批阅，你可以继续打卡学习，助教批阅完成后将会通知你。`
          : '';
      Modal.confirm({
        title: '确认提交吗？',
        content: subjectQuestion,
        okButtonProps: { loading: confirmLoading },
        onOk() {
          setConfirmLoading(true);
          let storedArray = JSON.parse(localStorage.getItem(cacheName) || '');
          postNodeRequest.run(storedArray);
        },
        onCancel() {
          console.log('取消提交');
        },
      });
    } catch (error: any) {
      if (error.errorFields && error.errorFields.length > 0) {
        const firstErrorField = error.errorFields[0].errors[0];
        let prefix = firstErrorField.substring(3, 7);
        if (prefix === 'cret') {
          message.warning('请完成职业认证');
        } else {
          message.warning('请填写必填字段');
        }
        // console.log('必填字段error', firstErrorField);
        // console.log('prefix', prefix);
      }

      handleFinishFailed(error);
    }
  };

  const renderedItems = useMemo(() => {
    return contentData.map((item, index) => (
      <div key={index}>{handleContentItem(item, index)}</div>
    ));
  }, [contentData]);

  const editDynamic = useMemo(() => {
    return (
      <Editor
        key={`editDynamic_${nodeId}`}
        style={{ height: 330, border: '1px solid #d6dfe7', maxWidth: 1260 }}
        content={
          nodeContentData?.nodeDynamic.content &&
          editParseJson(nodeContentData?.nodeDynamic.content)
        }
        titleProps={{ showTitle: false }}
        catalogProps={{ showCatalog: false }}
        onChange={(data) => {
          dynamicContentRef.current = JSON.stringify(data);
          debouncedUpdate();
        }}
        imageProps={{
          onImageUpload: onImageUpload,
        }}
        attachProps={{
          onAttachUpload: onFileAndVideoUpload,
        }}
        videoProps={{
          onVideoUpload: onFileAndVideoUpload,
        }}
      />
    );
  }, [contentData, nodeContentData]);

  const editRate = useMemo(() => {
    return (
      <Editor
        key={`editRate_${nodeId}`}
        style={{ height: 330, border: '1px solid #d6dfe7', maxWidth: 1260 }}
        content={
          nodeContentData?.studyNode.rateContent &&
          editParseJson(nodeContentData?.studyNode.rateContent)
        }
        titleProps={{ showTitle: false }}
        catalogProps={{ showCatalog: false }}
        onChange={(data) => {
          rateContentRef.current = JSON.stringify(data);
          debouncedUpdate();
        }}
        imageProps={{
          onImageUpload: onImageUpload,
        }}
        attachProps={{
          onAttachUpload: onFileAndVideoUpload,
        }}
        videoProps={{
          onVideoUpload: onFileAndVideoUpload,
        }}
      />
    );
  }, [contentData, nodeContentData]);

  return (
    <>
      {contextHolder}
      <div>
        {loading && (
          <div style={{ padding: 24 }}>
            <div style={{ width: '90%', marginBottom: 24, padding: '10px 0' }}>
              <Skeleton.Button active block style={{ height: 40 }} />
            </div>

            <div className="flex_0" style={{ width: '90%' }}>
              <div style={{ width: 120 }}>
                <Skeleton.Image active />
              </div>
              <div style={{ flex: 1 }}>
                <Skeleton active loading={loading} paragraph={{ rows: 6 }} />
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div>
            <div className={cx(styles.top_node_title, 'flex_3')}>
              <div className={styles.node_title}>{nodeContentData?.name}</div>
              <div className={styles.top_node_state}>本节习题全部正确哦～非常厉害</div>
            </div>
            <div className={styles.node_content_main}>
              <div className={styles.node_content}>
                {contentData && renderedItems}
                <div className={styles.new_antd_anchor}>
                  <Anchor items={anchorItems} />
                </div>

                <div className={styles.camp_rate_box}>
                  <div style={{ marginBottom: 40 }}>
                    <div id="rate_camp" className="f24 fw600" style={{ marginBottom: 16 }}>
                      打卡心得
                    </div>
                    <div className="com_edit_max_width">{editDynamic}</div>
                  </div>
                  {nodeContentData?.remark !== undefined && nodeContentData?.remark > 0 && (
                    <>
                      <div>
                        <div id="rate_study" className="f24 fw600" style={{ marginBottom: 16 }}>
                          学习评价
                        </div>
                        <div id="rate_study_content" className="flex_1 f14" style={{ gap: 16 }}>
                          <span className="com_required">【学习内容】方面：</span>
                          <span>
                            <Rate
                              style={{ color: '#FAAD14', fontSize: 26 }}
                              tooltips={rateText}
                              onChange={(value) => {
                                rateStudyContentRef.current = value;
                                setRateStudyContent(value);
                                debouncedUpdate();
                              }}
                              value={rateStudyContent}
                            />
                          </span>
                          <span className="colffa">
                            {rateStudyContent ? (
                              <span>{rateText[rateStudyContent - 1]}</span>
                            ) : null}
                          </span>
                        </div>
                      </div>

                      <div
                        id="rate_exercise_task"
                        className="flex_1 f14"
                        style={{ gap: 16, margin: '16px 0' }}
                      >
                        <span className="com_required">【习题任务】方面：</span>
                        <span>
                          <Rate
                            style={{ color: '#FAAD14', fontSize: 26 }}
                            tooltips={rateText}
                            onChange={(value) => {
                              rateExerciseTaskRef.current = value;
                              setRateExerciseTask(value);
                              debouncedUpdate();
                            }}
                            value={rateExerciseTask}
                          />
                        </span>
                        <span className="colffa">
                          {rateExerciseTask ? <span>{rateText[rateExerciseTask - 1]}</span> : null}
                        </span>
                      </div>

                      <div className="f14" style={{ margin: '24px 0 16px' }}>
                        对于今天的课程或者习题你有什么建议或者体验不好的地方嘛
                      </div>
                      <div className="com_edit_max_width">{editRate}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {!loading && (
          <div className={styles.node_post_main}>
            <div className="flex_1">
              <Button
                style={{ width: 140, height: 38, fontSize: 16, marginRight: 18 }}
                type="primary"
                loading={confirmLoading}
                onClick={() => {
                  handleSubmit();
                }}
              >
                提交
              </Button>

              <div className="f14">
                {saveText && (
                  <div>
                    {saveText === '自动保存成功' ? (
                      <div className="flex_1 col52c" style={{ gap: 4 }}>
                        <CheckCircleOutlined style={{ fontSize: 15 }} />
                        <span>{saveText}</span>
                      </div>
                    ) : saveText === '网络异常，将在网络恢复后自动保存' ? (
                      <div className="flex_1 col808" style={{ gap: 4 }}>
                        <InfoCircleOutlined style={{ fontSize: 15 }} />
                        <span>{saveText}</span>
                      </div>
                    ) : (
                      <div className="flex_1 col808" style={{ gap: 4 }}>
                        <SyncOutlined style={{ fontSize: 15 }} />
                        <span>{saveText}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default memo(StudyEdit);
